/**
 * Locations.
 *
 * A location is a place the business operates: a shop with a door, or a
 * mobile sales point that takes stock out and brings money back. Internally
 * these are `branches` — the table sales, stock, expenses, counts and
 * receipts have always pointed at — and nothing about that changes here. What
 * the shopkeeper reads is "location", because that is the word that covers
 * both a shop and a van.
 *
 * The type is the load-bearing field. "Issued out and not yet remitted" is a
 * question only an external location raises, so consignment tracking is built
 * on this distinction existing in the data.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Link as LinkIcon, MapPin, Plus, Truck, Users } from 'lucide-react';
import { LoginAccessDialog } from './LoginAccess';
import { useAllLocationLogins } from './loginData';
import {
  COUNTRIES,
  LOCATION_TYPES,
  LOCATION_TYPE_LABELS,
  findCountry,
  statesOf,
  type Branch,
  type LocationType,
  type OperatingCountries,
  type StaffLocation,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  Spinner,
} from '../../components/ui';

interface StaffMember {
  id: string;
  fullName: string;
  branchId: string | null;
}

/**
 * The link a worker opens to reach this location.
 *
 * Built from the location's id rather than its name or code, so renaming a
 * location never breaks a link somebody already sent. It is not a credential
 * and carries no secret: anyone may hold it, and CenoX decides what they see
 * from who signs in and which locations they are assigned to.
 */
function workerLink(branchId: string): string {
  return `${window.location.origin}/w/${branchId}`;
}

function CopyWorkerLink({ branchId }: { branchId: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    const link = workerLink(branchId);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard access is refused in plenty of ordinary situations — an
      // insecure origin, a browser setting, a locked-down device. Falling back
      // to a prompt means the owner still leaves with the link in hand.
      window.prompt('Copy this link for workers at this location:', link);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button size="sm" variant="ghost" onClick={copy} title={workerLink(branchId)}>
      <LinkIcon className="size-3.5" aria-hidden />
      {copied ? 'Copied' : 'Copy worker link'}
    </Button>
  );
}

export function LocationsPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<Branch | 'new' | null>(null);
  const [managingLogin, setManagingLogin] = useState<Branch | null>(null);
  const [assigning, setAssigning] = useState<StaffMember | null>(null);
  const [error, setError] = useState<string | null>(null);

  const locations = useQuery({
    queryKey: ['branches', business?.businessId],
    queryFn: () => api.get<{ data: Branch[] }>(path('/branches')),
    enabled: Boolean(business),
  });

  const countries = useQuery({
    queryKey: ['branch-countries', business?.businessId],
    queryFn: () => api.get<OperatingCountries>(path('/branches/countries')),
    enabled: Boolean(business),
  });

  /*
    Which locations already have a login. Read once for the whole list rather
    than per row, so adding a tenth location does not add a tenth request.
  */
  const allLogins = useAllLocationLogins(Boolean(business) && can('members:read'));
  const loginFor = (branchId: string) =>
    allLogins.data?.data.find((l) => l.branchId === branchId) ?? null;

  // Active staff only: this list decides who works where, and somebody who has
  // been deactivated works nowhere until they are reactivated.
  const staff = useQuery({
    queryKey: ['staff', business?.businessId, 'active'],
    queryFn: () =>
      api.get<{ data: StaffMember[] }>(path('/staff?pageSize=100&employmentStatus=active')),
    enabled: Boolean(business) && can('staff:read'),
  });

  const save = useMutation({
    mutationFn: (input: {
      id?: string;
      name: string;
      type: LocationType;
      isActive?: boolean;
      code?: string | null;
      countryCode: string | null;
      stateCode: string | null;
    }) =>
      input.id
        ? api.patch(path(`/branches/${input.id}`), {
            name: input.name,
            type: input.type,
            code: input.code ?? null,
            countryCode: input.countryCode,
            stateCode: input.stateCode,
            ...(input.isActive === undefined ? {} : { isActive: input.isActive }),
          })
        : api.post(path('/branches'), {
            name: input.name,
            type: input.type,
            ...(input.code ? { code: input.code } : {}),
            ...(input.countryCode ? { countryCode: input.countryCode } : {}),
            ...(input.stateCode ? { stateCode: input.stateCode } : {}),
          }),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save this location.'),
  });

  if (locations.isPending) return <Spinner label="Loading locations" />;
  if (locations.isError) {
    return <ErrorState message="Could not load locations." onRetry={() => void locations.refetch()} />;
  }

  const rows = locations.data?.data ?? [];

  return (
    <div className="space-y-4 pb-10">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Locations</h1>
          <p className="text-sm text-slate-500">
            The shops, stores and mobile teams this business operates from.
          </p>
        </div>
        {can('branches:create') && (
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" />
            Add location
          </Button>
        )}
      </header>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          {error}
        </div>
      )}

      {countries.data?.isMultiCountry && <OperatingCountriesCard countries={countries.data} />}

      {rows.length === 0 ? (
        <EmptyState
          title="No locations yet"
          description="Every business has at least one. Add the shop you trade from."
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className={`rounded-xl border p-3 ${
                row.isActive ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 font-medium text-slate-900">
                    {row.type === 'external_mobile' ? (
                      <Truck className="h-4 w-4 text-indigo-600" aria-hidden />
                    ) : (
                      <MapPin className="h-4 w-4 text-slate-400" aria-hidden />
                    )}
                    {row.name}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {LOCATION_TYPE_LABELS[row.type]}
                    {row.code && <> · {row.code}</>}
                    {row.address && <> · {row.address}</>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {row.isDefault && <Badge tone="info">Default</Badge>}
                  {!row.isActive && <Badge tone="neutral">Inactive</Badge>}
                  {loginFor(row.id) && !loginFor(row.id)!.isActive && (
                    <Badge tone="warning">Login disabled</Badge>
                  )}
                  {/*
                    Only for a location somebody can actually work in. Handing
                    out a link to a closed location would be handing out a door
                    that opens onto a refusal.
                  */}
                  {row.isActive && <CopyWorkerLink branchId={row.id} />}
                  {/*
                    Only where somebody can actually work: a login for a closed
                    location is a credential that opens onto a refusal.
                  */}
                  {row.isActive && can('members:read') && (
                    <Button size="sm" variant="ghost" onClick={() => setManagingLogin(row)}>
                      <KeyRound className="h-3.5 w-3.5" />
                      {loginFor(row.id) ? 'Login & Access' : 'Create login'}
                    </Button>
                  )}
                  {can('branches:update') && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {can('staff:read') && (
        <Card>
          <CardHeader
            title="Who works where"
            subtitle="A person can cover more than one location."
          />
          <div className="p-4 pt-0">
            {staff.isPending && <Spinner label="Loading staff" />}
            {staff.data?.data.length === 0 && (
              <EmptyState
                title="No staff yet"
                description="Add people under Staff to assign them to locations."
              />
            )}
            <ul className="space-y-2">
              {staff.data?.data.map((member) => (
                <li
                  key={member.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3"
                >
                  <span className="min-w-0 truncate text-sm font-medium text-slate-900">
                    {member.fullName}
                  </span>
                  <div className="flex items-center gap-2">
                    <StaffLocationSummary staffId={member.id} />
                    {can('staff:manage') && (
                      <Button size="sm" variant="ghost" onClick={() => setAssigning(member)}>
                        <Users className="h-3.5 w-3.5" />
                        Assign
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {managingLogin && (
        <LoginAccessDialog
          branchId={managingLogin.id}
          branchName={managingLogin.name}
          onClose={() => setManagingLogin(null)}
        />
      )}

      {editing && (
        <LocationForm
          value={editing === 'new' ? null : editing}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={({ name, type, isActive, code, countryCode, stateCode }) =>
            save.mutate({
              ...(editing === 'new' ? {} : { id: editing.id }),
              name,
              type,
              countryCode,
              stateCode,
              // Null clears it, a string sets it. An empty field on a location
              // that never had one must not write an empty string, because ''
              // is a code that nothing can ever match.
              code: code === '' ? null : code,
              ...(editing === 'new' ? {} : { isActive }),
            })
          }
        />
      )}

      {assigning && (
        <AssignLocations
          member={assigning}
          locations={rows}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  );
}

/** The locations a person covers, shown inline so the list is readable at a glance. */
function StaffLocationSummary({ staffId }: { staffId: string }): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();

  const assigned = useQuery({
    queryKey: ['staff-locations', business?.businessId, staffId],
    queryFn: () => api.get<{ data: StaffLocation[] }>(path(`/staff/${staffId}/locations`)),
    enabled: Boolean(business),
  });

  if (assigned.isPending) return <span className="text-xs text-slate-400">…</span>;

  const rows = assigned.data?.data ?? [];
  if (rows.length === 0) {
    // Not "none" as a number: nobody has decided yet, which is a different
    // state from having decided they work nowhere.
    return <span className="text-xs text-slate-400">Not assigned</span>;
  }

  return (
    <span className="text-xs text-slate-600">
      {rows
        .map((r) => (r.isPrimary ? `${r.branchName} (home)` : r.branchName))
        .join(', ')}
    </span>
  );
}

function LocationForm({
  value,
  saving,
  onClose,
  onSave,
}: {
  value: Branch | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: {
    name: string;
    type: LocationType;
    isActive: boolean;
    code: string;
    countryCode: string | null;
    stateCode: string | null;
  }) => void;
}): ReactNode {
  const { business } = useAuth();
  const [name, setName] = useState(value?.name ?? '');
  // '' is "the business's country", which is what null has always meant here.
  const [countryCode, setCountryCode] = useState(value?.countryCode ?? '');
  const [stateCode, setStateCode] = useState(value?.stateCode ?? '');
  const effectiveCountry = countryCode || business?.countryCode || '';
  const listed = statesOf(effectiveCountry);
  // Blank inherits the business's state, but only inside the business's country.
  const inheritsState = effectiveCountry === business?.countryCode;
  const businessCountry = business?.countryCode
    ? (findCountry(business.countryCode)?.name ?? business.countryCode)
    : 'the business';
  const [type, setType] = useState<LocationType>(value?.type ?? 'physical');
  const [isActive, setIsActive] = useState(value?.isActive ?? true);
  const [code, setCode] = useState(value?.code ?? '');

  return (
    <Modal open onClose={onClose} title={value ? 'Edit location' : 'Add location'}>
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Main Shop"
        />

        {/*
          The code another system calls this location.

          It had no field at all, which was fine while nothing outside CenoX
          needed to name a location. A delivery arriving from a dispatch system
          names one by code — never by name, because a rename would silently
          start filing one shop's deliveries under another — so an owner now
          needs to be able to see and set it.
        */}
        <Input
          label="Code"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          placeholder="FS"
          hint="How other systems refer to this location. Short, and worth leaving alone once anything uses it."
        />

        {/*
          Where the location is: its country, then the state inside it, from
          the same list the business's own state uses. What the Deliveries
          screen's selectors read. Blank inherits the business, as it always has.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Country"
            value={countryCode}
            onChange={(event) => {
              setCountryCode(event.target.value);
              setStateCode('');
            }}
            options={[
              { value: '', label: `Same as business (${businessCountry})` },
              ...COUNTRIES.map((c) => ({ value: c.code, label: c.name })),
            ]}
          />
          {listed.length > 0 && (
            <Select
              label={effectiveCountry === 'GH' ? 'Region' : effectiveCountry === 'KE' ? 'County' : 'State'}
              value={stateCode}
              onChange={(event) => setStateCode(event.target.value)}
              options={[
                { value: '', label: inheritsState ? 'Same as business' : 'Not set' },
                ...listed.map((r) => ({ value: r.code, label: r.name })),
              ]}
              hint="Also what a vehicle's default remittance is priced by."
            />
          )}
        </div>

        <Select
          label="Type"
          options={LOCATION_TYPES.map((t) => ({ value: t, label: LOCATION_TYPE_LABELS[t] }))}
          value={type}
          onChange={(event) => setType(event.target.value as LocationType)}
          hint={
            type === 'external_mobile'
              ? 'Stock is issued out to this location and money is remitted back.'
              : 'A place customers come to.'
          }
        />

        {value && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            Active
            <span className="text-xs text-slate-500">
              — turning this off hides it from new records; past ones are kept.
            </span>
          </label>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            onClick={() =>
              onSave({
                name: name.trim(),
                type,
                isActive,
                code: code.trim(),
                countryCode: countryCode || null,
                stateCode: stateCode || null,
              })
            }
            disabled={saving || name.trim() === ''}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Which locations a person works at.
 *
 * A set of checkboxes, sent whole — the form knows its final state, so it says
 * what that state IS rather than describing a change to it.
 */
function AssignLocations({
  member,
  locations,
  onClose,
}: {
  member: StaffMember;
  locations: Branch[];
  onClose: () => void;
}): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const current = useQuery({
    queryKey: ['staff-locations', business?.businessId, member.id],
    queryFn: () => api.get<{ data: StaffLocation[] }>(path(`/staff/${member.id}/locations`)),
  });

  const [chosen, setChosen] = useState<string[] | null>(null);
  const [home, setHome] = useState<string | null>(null);

  const rows = current.data?.data;
  if (rows && chosen === null) {
    setChosen(rows.map((r) => r.branchId));
    setHome(rows.find((r) => r.isPrimary)?.branchId ?? null);
  }

  const save = useMutation({
    mutationFn: () =>
      api.put(path(`/staff/${member.id}/locations`), {
        branchIds: chosen ?? [],
        primaryBranchId: home,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff-locations'] });
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the assignment.'),
  });

  const toggle = (id: string): void => {
    setChosen((ids) => {
      const set = new Set(ids ?? []);
      if (set.has(id)) {
        set.delete(id);
        if (home === id) setHome(null);
      } else {
        set.add(id);
      }
      return [...set];
    });
  };

  return (
    <Modal open onClose={onClose} title={`Where ${member.fullName} works`}>
      <div className="space-y-3">
        {current.isPending && <Spinner label="Loading assignment" />}

        {error && (
          <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-900">
            {error}
          </p>
        )}

        <ul className="space-y-1.5">
          {locations.map((location) => {
            const on = (chosen ?? []).includes(location.id);
            return (
              <li key={location.id} className="flex items-center justify-between gap-2">
                <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                  <input type="checkbox" checked={on} onChange={() => toggle(location.id)} />
                  <span className="truncate">{location.name}</span>
                  <span className="text-xs text-slate-400">
                    {LOCATION_TYPE_LABELS[location.type]}
                  </span>
                </label>
                {on && (
                  <label className="flex shrink-0 items-center gap-1 text-xs text-slate-600">
                    <input
                      type="radio"
                      name="home-location"
                      checked={home === location.id}
                      onChange={() => setHome(location.id)}
                    />
                    Home
                  </label>
                )}
              </li>
            );
          })}
        </ul>

        <p className="text-xs text-slate-500">
          This records where {member.fullName} works. It does not decide what their login can
          see — that is set against their account.
        </p>

        <div className="flex gap-2 pt-1">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}


/**
 * Where the business operates.
 *
 * Derived from the locations themselves, so it cannot disagree with the list
 * above it. Silent for the single-country business it would describe nothing
 * useful about. Moved here from Settings when locations got their own screen.
 */
function OperatingCountriesCard({ countries }: { countries: OperatingCountries }): ReactNode {
  return (
    <Card>
      <CardHeader
        title="Where you operate"
        subtitle="Taken from your locations — there is no separate list to keep in step"
      />
      <div className="p-4 pt-0">
        <ul className="space-y-2">
          {countries.countries.map((entry) => (
            <li key={entry.countryCode} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-900">
                {findCountry(entry.countryCode)?.name ?? entry.countryCode}
              </span>
              {entry.isRegistered && <Badge tone="info">Registered here</Badge>}
              <span className="text-slate-500">
                {entry.branchCount} {entry.branchCount === 1 ? 'location' : 'locations'} ·{' '}
                {entry.currencyCodes.join(', ')}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Each currency is reported in its own terms. Reports state one currency at a time and
          never add two together.
        </p>
      </div>
    </Card>
  );
}
